import { useState, useEffect } from 'react';
import { 
    CheckCircle2, AlertCircle, MessageSquare, Send, Loader2, Eye, Lightbulb, FileText, Users, Sparkles, LayoutDashboard, Clock,
    Shield, Play, Pause, Ban, RotateCcw, TestTube, Check, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ConfigItemActivityFeed } from './ConfigItemActivityFeed';
import { ConsultantAIDiscoveryTools } from './ConsultantAIDiscoveryTools';
import { ConsultantDiscoveryConsole } from './ConsultantDiscoveryConsole';
import { ScopeDeltaReview } from './ScopeDeltaReview';
import { GuidanceButton } from './GuidanceButton';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import type { HierarchicalConfigItem, Question } from '../types/phase1e';
import { toast } from 'sonner';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

import { Label } from './ui/label';
import { allowedMoves, labelFor, normalizeStatus, type LifecycleStatus } from '../utils/configItemLifecycle';
import { readyForBuildErrors } from '../utils/configItemReadiness';
import { ReadyForBuildBlockedDialog } from './ConfigItemStatusControl';

// The one lifecycle status (docs/CONFIG_ITEM_LIFECYCLE.md). Reopening discovery is the unlock on the grid.
const LIFECYCLE_STATES: Array<{ id: LifecycleStatus; label: string; icon: typeof FileText; color: string }> = [
    { id: 'discovery', label: labelFor('discovery'), icon: FileText, color: 'text-gray-500' },
    { id: 'ready_for_build', label: `${labelFor('ready_for_build')} (locked)`, icon: Shield, color: 'text-indigo-600' },
    { id: 'in_build', label: labelFor('in_build'), icon: Play, color: 'text-blue-600' },
    { id: 'ready_for_review', label: labelFor('ready_for_review'), icon: Eye, color: 'text-purple-600' },
    { id: 'approved', label: labelFor('approved'), icon: Check, color: 'text-green-600' },
    { id: 'blocked', label: labelFor('blocked'), icon: Ban, color: 'text-red-600' },
];

interface ConsultantReviewPanelProps {
  item: HierarchicalConfigItem;
  initialQuestionId?: string; // NEW: Focus on specific question
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  open: boolean;
  initialViewMode?: 'answers' | 'activity' | 'ai-tools' | 'discovery-console' | 'scope-review';
  onClose: () => void;
  onUpdate: () => void;
}

export function ConsultantReviewPanel({
  item,
  initialQuestionId,
  companyId,
  companyName,
  accessToken,
  currentUser,
  open,
  initialViewMode = 'answers',
  onClose,
  onUpdate,
}: ConsultantReviewPanelProps) {
  const [changeRequestMessage, setChangeRequestMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'answers' | 'activity' | 'ai-tools' | 'discovery-console' | 'scope-review'>(initialViewMode);
  
  // A move that needs a comment (approve on the client's behalf, send back) asks for it first.
  const [pendingStatus, setPendingStatus] = useState<LifecycleStatus | null>(null);
  const [lockBlockedBy, setLockBlockedBy] = useState<string[] | null>(null);
  const [moveComment, setMoveComment] = useState('');

  // Update view mode if initialViewMode changes when opening
  useEffect(() => {
    if (open) {
      setViewMode(initialViewMode);
    }
  }, [open, initialViewMode]);

  const discoveryAnswers = item.discoveryData?.answers || {};
  
  // Resolve active question context
  const activeQuestionId = initialQuestionId;
  const definedQuestions = item.customQuestions || [];
  
  // Find question definition
  const activeQuestionDef = definedQuestions.find(q => q.id === activeQuestionId);
  const activeAnswer = activeQuestionId ? discoveryAnswers[activeQuestionId] : null;
  
  // Compute display text
  const getQuestionText = (id: string) => {
      if (activeQuestionDef) return activeQuestionDef.text; // Use canonical 'text' field, fallback to questionText for legacy
      // @ts-ignore - support legacy field if present during migration
      if (activeQuestionDef?.questionText) return activeQuestionDef.questionText;
      return formatCategoryLabel(id);
  };

  const activeQuestionText = activeQuestionId ? getQuestionText(activeQuestionId) : item.name;

  // The item's one lifecycle status and the moves the firm may make from it (the server applies the same table).
  const currentStatus = normalizeStatus(item);
  const moves = allowedMoves(currentStatus, { firm: true, blockedFrom: (item as any).blockedFrom });
  const stateFor = (id: string) => LIFECYCLE_STATES.find(s => s.id === id) || LIFECYCLE_STATES[0];

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus) return;
    // Locking for build waits until the item is ready (RESTORE_PLAN R2.8)
    if (currentStatus === 'discovery' && newStatus === 'ready_for_build') {
      const errors = readyForBuildErrors({ ...item, companyId });
      if (errors.length > 0) {
        setLockBlockedBy(errors);
        return;
      }
    }
    const move = moves.find(m => m.to === newStatus);
    if (!move) {
      toast.error(newStatus === 'discovery' ? 'Reopening discovery is the unlock on the grid (with a reason)' : `Cannot move this item from ${labelFor(currentStatus).toLowerCase()} to ${labelFor(newStatus).toLowerCase()}`);
      return;
    }
    if (move.requiresComment) {
      setPendingStatus(move.to);
      setMoveComment('');
      return;
    }
    await performStatusUpdate(move.to);
  };

  const performStatusUpdate = async (newStatus: LifecycleStatus, comment?: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${item.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus, comment }),
        }
      );

      if (response.ok) {
        toast.success(`Status updated to ${labelFor(newStatus).toLowerCase()}`);
        onUpdate();
        setPendingStatus(null);
        setMoveComment('');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(`Failed to update: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    } finally {
      setLoading(false);
    }
  };

  const confirmMove = () => {
      if (!pendingStatus) return;
      if (!moveComment.trim()) {
          toast.error('A comment is required for this move');
          return;
      }
      performStatusUpdate(pendingStatus, moveComment.trim());
  };

  const handleRequestChanges = async () => {
    if (!changeRequestMessage.trim()) return;

    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${item.id}/request-changes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: currentUser.id,
            userName: currentUser.name || currentUser.email,
            requestMessage: changeRequestMessage,
          }),
        }
      );

      if (response.ok) {
        console.log('Change request sent successfully');
        setChangeRequestMessage('');
        onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error requesting changes:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-[90vw] !max-h-[90vh] w-[90vw] h-[90vh] overflow-y-auto flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between pr-8">
            <div className="flex-1">
              {/* PRIMARY TITLE: Question Text (or Item Name if no specific question) */}
              <div className="flex items-center gap-3 flex-wrap">
                  <DialogTitle className="text-2xl leading-snug">
                    {activeQuestionText}
                  </DialogTitle>
                  
                  {/* GUIDANCE BUTTON - DOCTRINE REQUIREMENT */}
                  {activeQuestionDef && <GuidanceButton question={activeQuestionDef} />}
                  
                  {/* INTENT BADGE (Section 2.2) */}
                  {/* @ts-ignore - support legacy field if present */}
                  {activeQuestionDef?.intent === 'exploratory' && (
                      <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200">
                          <Lightbulb className="size-3 mr-1" /> Exploratory
                      </Badge>
                  )}
                  {/* @ts-ignore - support legacy field if present */}
                  {activeQuestionDef?.intent === 'baseline' && (
                      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                          <Shield className="size-3 mr-1" /> Baseline Scope
                      </Badge>
                  )}
              </div>
              
              {/* SECONDARY CONTEXT: Parent Item info */}
              <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                 <Badge variant="outline" className="text-xs font-normal">
                    {formatCategoryLabel(item.category || 'General')}
                 </Badge>
                 <span className="text-gray-400">•</span>
                 <span>{item.name}</span>
                 {item.isGlobal ? (
                    <Badge variant="secondary" className="text-[10px] h-5">Global</Badge>
                 ) : (
                    <Badge variant="outline" className="text-[10px] h-5">Space Specific</Badge>
                 )}
              </div>
            </div>
            
            {/* LIFECYCLE CONTROL (Micro-Gates) */}
            <div className="flex flex-col items-end gap-2">
               <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-lg border">
                   <span className="text-xs font-medium text-gray-500 ml-1 mr-1">Status:</span>
                                      <Select
                      value={currentStatus}
                      onValueChange={handleStatusChange}
                      disabled={loading}
                   >
                       <SelectTrigger className="h-8 w-[200px] text-xs bg-white border-gray-200 shadow-sm">
                           <SelectValue>
                               {(() => {
                                   const state = stateFor(currentStatus);
                                   const Icon = state.icon;
                                   return (
                                       <div className="flex items-center gap-2">
                                           <Icon className={`size-3.5 ${state.color}`} />
                                           <span>{state.label}</span>
                                       </div>
                                   );
                               })()}
                           </SelectValue>
                       </SelectTrigger>
                       <SelectContent>
                           {[currentStatus, ...moves.map(m => m.to)].map(id => stateFor(id)).map(state => (
                               <SelectItem key={state.id} value={state.id}>
                                   <div className="flex items-center gap-2">
                                       <state.icon className={`size-3.5 ${state.color}`} />
                                       <span>{state.label}</span>
                                   </div>
                               </SelectItem>
                           ))}
                       </SelectContent>
                   </Select>
               </div>
            </div>
          </div>

          {/* View toggle */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={viewMode === 'answers' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('answers')}
            >
              <Eye className="size-4 mr-2" />
              Client Answers
            </Button>
            <Button
              variant={viewMode === 'activity' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('activity')}
            >
              <MessageSquare className="size-4 mr-2" />
              Activity & Comments
            </Button>
            <Button
              variant={viewMode === 'ai-tools' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('ai-tools')}
            >
              <Lightbulb className="size-4 mr-2" />
              AI Tools
            </Button>
            <Button
              variant={viewMode === 'discovery-console' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('discovery-console')}
            >
              <LayoutDashboard className="size-4 mr-2" />
              Discovery Console
            </Button>
            <Button
              variant={viewMode === 'scope-review' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('scope-review')}
            >
              <FileText className="size-4 mr-2" />
              Scope Review
            </Button>
          </div>
        </DialogHeader>

        <Separator className="my-4" />

        {/* Content based on view mode */}
        {viewMode === 'answers' ? (
          <div className="space-y-6">
            
            {/* Display Single Active Question if focused */}
            {activeQuestionId ? (
                <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                        <FileText className="size-5 text-blue-600" />
                        Client Response
                    </h3>
                    
                    <div className="border rounded-lg p-5 space-y-3 bg-white shadow-sm">
                        {activeAnswer !== undefined && activeAnswer !== null && activeAnswer !== '' ? (
                            <div className="bg-gray-50 p-3 rounded-md border border-gray-100 text-gray-900 font-medium whitespace-pre-wrap">
                                {typeof activeAnswer === 'string' ? activeAnswer : JSON.stringify(activeAnswer, null, 2)}
                            </div>
                        ) : (
                            <div className="bg-amber-50 p-3 rounded-md border border-amber-100 text-amber-800 italic text-sm flex items-center gap-2">
                                <AlertCircle className="size-3.5" />
                                No response submitted yet
                            </div>
                        )}

                        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100 mt-2">
                            <div className="flex items-center gap-1.5">
                                <Users className="size-3.5" />
                                <span>Answered by: {item.discoveryData?.lastUpdatedBy || 'Unknown User'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock className="size-3.5" />
                                <span>Answered on: {item.discoveryData?.lastUpdatedAt ? new Date(item.discoveryData.lastUpdatedAt).toLocaleString() : 'Unknown Date'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* Fallback: List all questions if no specific focus (Legacy behavior) */
                <div className="space-y-4">
                    <div className="text-center py-8 text-gray-500">
                        Select a specific question to view details.
                    </div>
                </div>
            )}

            <Separator />

            {/* Request changes section */}
            {item.status === 'waiting_consultant_response' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Request Changes (Optional)</h3>
                <p className="text-sm text-muted-foreground">
                  If you need more information or clarification, ask the client for changes:
                </p>
                <Textarea
                  value={changeRequestMessage}
                  onChange={(e) => setChangeRequestMessage(e.target.value)}
                  placeholder="e.g., 'Can you provide more detail about the approval workflow? Specifically, who should be the approvers for requests over $5,000?'"
                  rows={4}
                />
              </div>
            )}
          </div>
        ) : viewMode === 'activity' ? (
          <ConfigItemActivityFeed
            itemId={item.id}
            itemName={item.name}
            companyId={companyId}
            accessToken={accessToken}
            currentUser={currentUser}
          />
        ) : viewMode === 'ai-tools' ? (
          <ConsultantAIDiscoveryTools
            item={item}
            companyId={companyId}
            companyName={companyName}
            accessToken={accessToken}
            consultingFirmId={currentUser?.firmId || currentUser?.consultingFirmId}
          />
        ) : viewMode === 'discovery-console' ? (
          <ConsultantDiscoveryConsole
            companyId={companyId}
            accessToken={accessToken}
          />
        ) : (
          <ScopeDeltaReview
            itemId={item.id}
            companyId={companyId}
            accessToken={accessToken}
            currentUser={currentUser}
          />
        )}

        <Separator className="my-4" />

        {/* Action buttons - LEGACY SUPPORT & TRANSITIONS */}
        <div className="flex items-center justify-between gap-4">
             {/* Show submission info if available */}
            <div className="text-sm text-muted-foreground">
               {item.submittedForReviewAt && (
                   <span>Submitted {new Date(item.submittedForReviewAt).toLocaleDateString()}</span>
               )}
            </div>

            <div className="flex items-center gap-2">
                {/* Legacy "Request Changes" flow - still useful for communication */}
                <Button
                    variant="outline"
                    onClick={handleRequestChanges}
                    disabled={!changeRequestMessage.trim() || loading}
                >
                    {loading ? <Loader2 className="size-4 mr-2 animate-spin" /> : <AlertCircle className="size-4 mr-2" />}
                    Request Info
                </Button>
                
                {/* Quick advance (the firm's next move in the lifecycle) */}
                {currentStatus === 'discovery' && (
                    <Button onClick={() => handleStatusChange('ready_for_build')} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                        <Shield className="size-4 mr-2" />
                        Lock for build
                    </Button>
                )}
                {currentStatus === 'ready_for_build' && (
                    <Button onClick={() => handleStatusChange('in_build')} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                        <Play className="size-4 mr-2" />
                        Start build
                    </Button>
                )}
                {currentStatus === 'in_build' && (
                    <Button onClick={() => handleStatusChange('ready_for_review')} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                        <Eye className="size-4 mr-2" />
                        Hand to review
                    </Button>
                )}
            </div>
        </div>
      </DialogContent>
    </Dialog>
    
    <Dialog open={!!pendingStatus} onOpenChange={(open) => { if (!open) setPendingStatus(null); }}>
      <DialogContent className="max-w-md">
         <DialogHeader>
            <DialogTitle>{pendingStatus === 'approved' ? "Approve on the client's behalf" : 'Send back to build'}</DialogTitle>
            <DialogDescription>
                {pendingStatus === 'approved' ? 'The client confirmed the build outside the app; note where.' : 'The build is not right yet; say what to change.'}
            </DialogDescription>
         </DialogHeader>
         <div className="space-y-2 py-2">
             <Label htmlFor="review-panel-move-comment">Comment <span className="text-red-500">*</span></Label>
             <Textarea
                id="review-panel-move-comment"
                value={moveComment}
                onChange={(e) => setMoveComment(e.target.value)}
                placeholder={pendingStatus === 'approved' ? 'Confirmed on the 29 Oct call…' : 'What needs to change…'}
                rows={3}
             />
         </div>
         <div className="flex justify-end gap-2 pt-2">
             <Button variant="ghost" onClick={() => setPendingStatus(null)}>Cancel</Button>
             <Button onClick={confirmMove} disabled={loading || !moveComment.trim()}>Confirm</Button>
         </div>
      </DialogContent>
    </Dialog>
    <ReadyForBuildBlockedDialog errors={lockBlockedBy} itemName={item?.name} onClose={() => setLockBlockedBy(null)} />
    </>
  );
}